import { Component, EventEmitter, Input, NgZone, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ICheckbox, IDropdown, IToast, LayoutService, ToastService } from 'codex-lib';
import { of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { ParticipantTypeEnum } from 'src/app/enum/participant.enum';
import { SubscriberTypeEnum } from 'src/app/enum/subscribe-type-enum';
import { SignatureType as SignatureTypeEnum } from 'src/app/enum/signature-type';
import { Signatory } from 'src/app/envelope/model/signatory';
import { SignatoryEdit } from 'src/app/envelope/model/signatory-edit';
import { TableSignUserBase } from 'src/app/envelope/model/table-sign-user-base';
import { SignatoryService } from 'src/app/envelope/services/signatory.service';
import { SignerUserModel } from 'src/app/model/signer-user-model';
import { SegmentViewModel } from 'src/app/modules/settings/model/segment-settings-model';
import { SegmentService } from 'src/app/modules/settings/services/segment.service';
import { AuthService } from 'src/app/services/auth.service';
import { SharedService } from 'src/app/services/shared-service.service';
import { SurvicateService } from 'src/app/services/survicate.service';
import { CPFCNPJUtil } from 'src/app/util/CPF-CNPJ-util';
import { FormGroupUtil } from 'src/app/util/form-group-util';
import { IdentificationCodeUtil } from 'src/app/util/identification-code-util';
import { LanguageUtil } from 'src/app/util/language-util';
import { RegexUtil } from 'src/app/util/regex-util';
import { StringUtil } from 'src/app/util/string-util';
import { WhatasappSettingsService } from 'src/app/modules/settings/services/whatsapp-settings.service';
import { ApiUrlUtil } from 'src/app/util/api-url-util';
import { SignatoryTypeRoleService } from 'src/app/modules/contacts/services/signature-type-role.service';
import { SignatoryTypeRoleFilter } from 'src/app/modules/contacts/model/signatory-type-role-filter';
import { SignatoryTypeRole } from 'src/app/modules/contacts/model/signatory-type-role';

@Component({
  selector: 'app-contact-form',
  templateUrl: './contact-form.component.html',
  styleUrls: ['./contact-form.component.scss']
})
export class ContactFormComponent implements OnInit, OnChanges {
  checkboxOptions: ICheckbox = {
    span: '',
    checkboxSection: false
  };

  @Output() modalCloseEmitter = new EventEmitter();
  @Output() addSignatoryToTable = new EventEmitter();

  @Input() participantType: ParticipantTypeEnum;
  @Input() userToEdit!: any;
  @Input() elements: Array<any>;

  signatoryAddForm: FormGroup;
  tableSignUserBase: TableSignUserBase;
  subscriberTypeDropDown: IDropdown;
  signatureTypeDropDown: IDropdown;
  ssnMask: string;
  activeModal: NgbActiveModal;

  signatoryUser = { fullName: '', mail: '', ssn: '', subType: '', signType: '', telephone: '' };
  hiddenSSN = false;
  disabledSSN = false;
  segments: Array<SegmentViewModel>;
  allSegments = true;
  isSignFree = false;
  isSignFreemium = false;
  defaultValuePhone: string;
  telephoneValue: string;
  acceptWhatsapp: boolean;
  phoneValid: boolean;
  signerUserModel: SignerUserModel;
  signatureWarningLitmitCaracter: boolean = false;
  isObserverOrAuthorizer: boolean = false;
  isResponsible: boolean = false;
  isEditEnvelope: boolean = false;
  signatories: Array<SignatoryEdit> = [];
  isSendMessageWhatsappEnabled: boolean;
  isSendWhatsapp: boolean = false;
  loading: boolean;
  private formInitialized: boolean = false;

  get isWhatsappRequirementMet(): boolean {
    const isWhatsappActive = this.acceptWhatsapp && 
                             this.isSendMessageWhatsappEnabled && 
                             this.isSendWhatsapp;

    if (!isWhatsappActive) {
      return true; 
    }

    return !!this.telephoneValue && this.phoneValid;
  }

  get canSubmitSignatory(): boolean {
    return this.signatoryAddForm.valid && 
           this.segmentFormValidate() && 
           this.isWhatsappRequirementMet;
  }

  constructor(
    private authService: AuthService,
    private translateService: TranslateService,
    private ngZone: NgZone,
    private signatoryService: SignatoryService,
    private segmentService: SegmentService,
    private layoutService: LayoutService,
    private survicateService: SurvicateService,
    private toastService: ToastService,
    private sharedService: SharedService,
    private whatsappSettingsService: WhatasappSettingsService,
    private signatoryTypeRoleService: SignatoryTypeRoleService
  ) {
    this.isEditEnvelope = this.sharedService.getIsEditEnvelope();

    if (!this.authService.signUserModel) {
      return;
    }

    this.signerUserModel = this.authService.signUserModel;
    this.tableSignUserBase = new TableSignUserBase(translateService);
  }

  async ngOnInit() {
    this.loading = true;

    this.isSendMessageWhatsappEnabled = await this.whatsappSettingsService.getSendMessageWhatsapp().toPromise();

    if (this.isSendMessageWhatsappEnabled) {
      this.isSendWhatsapp = await this.whatsappSettingsService.getSendingByWhatsapp().toPromise();
    }

    this.isObserverOrAuthorizer = this.participantType !== ParticipantTypeEnum.Signatory;
    this.isResponsible = this.participantType === ParticipantTypeEnum.Responsible;

    const signatoryTypeRolefilter = Object.assign(new SignatoryTypeRoleFilter(), {
      skip: 0,
      take: 500,
      active: true,
      inactive: false
    });

    const signatoryTypeRoles = await this.signatoryTypeRoleService.getWithFilter(signatoryTypeRolefilter, false).toPromise();

    this.tableSignUserBase.createInitObject().pipe(take(1)).subscribe(x => x.subscribe(() => {
      this.isSignFreemium = this.survicateService.isSignFree();
      this.checkLanguageEnvelope(this.translateService.currentLang);

      const subTypeValidators = [];
      if (!this.isObserverOrAuthorizer) {
        subTypeValidators.push(Validators.required);
      }
      subTypeValidators.push(this.subscriberTypeValidator.bind(this));

      this.signatoryAddForm = new FormGroup({
        fullName: new FormControl(this.signatoryUser.fullName, [
          Validators.required,
          Validators.maxLength(500)
        ]),
        mail: new FormControl(this.signatoryUser.mail, [
          Validators.required,
          Validators.pattern(RegexUtil.EMAIL),
          Validators.maxLength(300)
        ]),
        ssn: new FormControl(this.signatoryUser.ssn, [
          Validators.pattern(CPFCNPJUtil.getPattern())
        ]),
        subType: new FormControl(this.signatoryUser.subType, subTypeValidators),
        signType: new FormControl(this.signatoryUser.signType, !this.isObserverOrAuthorizer ? [
          Validators.required
        ] : null),
        telephoneValue: new FormControl(this.signatoryUser.telephone || '', this.acceptWhatsapp ? Validators.required : [])
      });

      this.subscriberTypeDropDown = {
        items: signatoryTypeRoles,
        displayText: 'name',
        placeholder: this.translateService.instant('selectAnItem'),
        showSearch: true
      };

      this.signatureTypeDropDown = {
        items: this.tableSignUserBase.signatureType.List(),
        displayText: 'mask',
        placeholder: this.translateService.instant('selectAnItem'),
        showSearch: false
      };

      this.subscribeOnChangeSsnValue();
      this.formInitialized = true;

      if (this.userToEdit) {
        this.loadUserData(this.userToEdit);
      } else {
        this.setSegments();
      }

      this.loading = false;
    }));
  }

  get fullName() {
    return this.signatoryAddForm.get('fullName');
  }
  get mail() {
    return this.signatoryAddForm.get('mail');
  }
  get ssn() {
    return this.signatoryAddForm.get('ssn');
  }
  get subscriberType() {
    return this.signatoryAddForm.get('subType');
  }
  get signatureType() {
    return this.signatoryAddForm.get('signType');
  }
  get telephone() {
    return this.signatoryAddForm.get('telephoneValue');
  }

  linkClicked() {
    ApiUrlUtil.goToKnowledgeBase();
  }

  checkLanguageEnvelope(language) {
    if (!LanguageUtil.isCurrentLanguagePT(language)) {
      this.hiddenSSN = true;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.userToEdit && changes.userToEdit.currentValue && this.formInitialized) {
      this.loadUserData(changes.userToEdit.currentValue);
    }
  }

  private loadUserData(user: any) {
    if (!this.signatoryAddForm) {
      return;
    }

    const hasEnvSignId =
      typeof user?.envelopeSignatoryId === 'number' &&
      Number.isInteger(user.envelopeSignatoryId) &&
      user.envelopeSignatoryId > 0;

    if (!this.isEditEnvelope || (!hasEnvSignId && !user.saveJustInEnvelope)) {
      this.signatoryService.get(user.id, false).subscribe((data) => {
        this.fillForm(user, data);
      });
    } else {
      this.fillForm(user, user);
    }
  }

  private fillForm(user: any, data: any) {
    if (!this.signatoryAddForm) {
      return;
    }

    const sigId = typeof user.signatureType === 'object'
      ? user.signatureType.id
      : +user.signatureType;

    const subId = typeof user.subscriberType === 'object'
      ? user.subscriberType.id
      : +user.subscriberType;
    
    this.fullName.setValue(user.name);
    this.telephone.setValue(user.phoneNumber);
    this.mail.setValue(data.mail);
    this.ssn.setValue(CPFCNPJUtil.formatSsn(user.ssn));
    this.signatureType.setValue(
      this.tableSignUserBase.signatureType.List().find(x => x.id === sigId)
    );
    this.signatureType.setErrors(null);
    
    const signatoryTypeRoleRaw: SignatoryTypeRole = data.signatoryTypeRole
      ?? (typeof data.subscriberType === 'object' ? data.subscriberType : null)
      ?? (this.subscriberTypeDropDown?.items as SignatoryTypeRole[])
          ?.find(x => x.id === (data.signatoryTypeRoleId ?? data.subscriberType));

    if (!signatoryTypeRoleRaw) {
      return;
    }

    const signatoryTypeRole = { 
      id: signatoryTypeRoleRaw.id, 
      name: signatoryTypeRoleRaw.name, 
      isActive: signatoryTypeRoleRaw.isActive
    } as SignatoryTypeRole;

    this.subscriberType.setValue(signatoryTypeRole);
    this.subscriberType.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    if (this.subscriberType.invalid) {
      this.subscriberType.markAsTouched();
      this.subscriberType.markAsDirty();
    }
    this.ssn.disable();
    this.hiddenSSN = this.ssn.value === '' || this.ssn.value === '-';
    this.setSegmentsElementSelected(data.segments);
    this.allSegments = data.allSegments;
    const { phoneNumber, acceptWhatsApp } = this.userToEdit ? data : user;
    this.telephoneValue = phoneNumber;
    this.defaultValuePhone = phoneNumber;
    this.acceptWhatsapp = acceptWhatsApp;
  }

  setSegments() {
    this.layoutService.toggleLoading(true);
    this.segmentService
      .getSegments(false)
      .pipe(
        switchMap((ev: Array<SegmentViewModel>) => {
          ev.forEach(x => {
            x.checked = true;
          });
          this.segments = ev;
          if (this.segments.some(x => x.isSignFree)) {
            this.isSignFree = true;
            this.allSegments = false;
          }
          return of(true);
        }),
      )
      .subscribe(x => {
        this.layoutService.toggleLoading(false);
      },
        err => {
          this.layoutService.toggleLoading(false);
        }
      );
  }

  setSegmentsElementSelected(segmentsElement: any) {
    if (segmentsElement) {
      let segmentsContactId = segmentsElement.map(e => e.segmentId ?? e.id);
      this.layoutService.toggleLoading(true);
      this.segmentService
        .getSegments(false)
        .pipe(
          switchMap((ev: Array<SegmentViewModel>) => {
            ev.forEach(x => {
              x.checked = segmentsContactId.some(id => id === x.id) || this.allSegments;
            });
            this.segments = ev;
            if (this.segments.some(x => x.isSignFree)) {
              this.isSignFree = true;
              this.allSegments = false;
            }
            return of(true);
          }),
        )
        .subscribe(x => {
          this.layoutService.toggleLoading(false);
        },
          err => {
            this.layoutService.toggleLoading(false);
          }
        );
    }
  }

  segmentChecked(segment: SegmentViewModel) {
    if (!this.allSegments) {
      this.segments.find(obj => obj.id === segment.id).checked = !segment.checked;
    }
  }

  segmentFormValidate() {
    if (this.segments) {
      return this.segments.some(x => x.checked === true) || this.allSegments;
    }
    return false;
  }

  onAllSegmentChecked() {
    this.allSegments = !this.allSegments;
    this.segments.forEach(x => {
      x.checked = this.allSegments;
    });
  }

  changeHasSnn(value) {
    this.disabledSSN = !value;
    FormGroupUtil.disableOrEnableSpecificControl(this.signatoryAddForm, 'ssn', this.disabledSSN);
  }

  close() {
    this.formInitialized = false;
    this.cleanForm();
    setTimeout(() => this.modalCloseEmitter.emit(), 0);
  }

  phoneNumberChange(phoneNumber: string) {
    this.telephoneValue = phoneNumber;
    const phoneControl = this.signatoryAddForm.get('telephoneValue');
    phoneControl.setValue(this.telephoneValue);

    if (this.acceptWhatsapp) {
      phoneControl.setValidators([Validators.required]);
    } else {
      phoneControl.clearValidators();
    }
    phoneControl.updateValueAndValidity();
  }

  async addSignatory(isSaveContact: boolean): Promise<void> {
    StringUtil.validatedName(this.fullName);

    if (!this.signatoryAddForm.valid) return;

    let existingData: any = null;
    if (this.userToEdit && (typeof this.userToEdit.id === 'string' && !this.userToEdit.id.startsWith('temp-'))) {
      this.layoutService.toggleLoading(true);
      existingData = await this.signatoryService
        .get(this.userToEdit.id, true)
        .pipe(take(1))
        .toPromise();
      this.layoutService.toggleLoading(false);
    }

    const contact = this.buildContactFromForm(existingData);
    if (!(await this.validateBeforeSave()))
      return;

    contact.saveTo = isSaveContact;
    if (isSaveContact) {
      await this.saveToContact(contact, !!this.userToEdit);
    } else {
      this.saveLocally(contact);
    }
  }

  private buildContactFromForm(existingData?: any): Signatory {
    const c = new Signatory();

    c.name = this.fullName.value;
    c.mail = this.mail.value;
    c.ssn = this.ssn.value;
    c.phoneNumber = this.telephoneValue;
    c.acceptWhatsApp = this.acceptWhatsapp;
    c.active = true;

    if (existingData && this.isObserverOrAuthorizer) {
      c.subscriberType = {
        id: existingData.subscriberType,
        value: SubscriberTypeEnum[existingData.subscriberType]
      };
      c.signatureType = {
        id: existingData.signatureType,
        value: SignatureTypeEnum[existingData.signatureType]
      };
      c.allSegments = existingData.allSegments;
      c.segments = existingData.segments.map(s => ({ id: s.segmentId }));
    } else {
      c.subscriberType = { id: 0, value: 'Signatory' };

      c.signatoryTypeRoleId = (this.isObserverOrAuthorizer || this.isResponsible) ? 1 : this.subscriberType.value.id;

      c.signatureType = !this.isObserverOrAuthorizer
        ? Object.assign({}, this.signatureType.value)
        : this.isResponsible 
        ? { id: 0, value: 'Eletronic or Digital' } 
        : { id: 2, value: 'Eletronic' };

      c.allSegments = !this.isObserverOrAuthorizer
        ? this.allSegments
        : true;

      c.segments = !this.isObserverOrAuthorizer
        ? this.segments?.filter(x => x.checked).map(({ id }) => ({ id }))
        : this.segments?.map(({ id }) => ({ id }));
    }

    return c;
  }

  private async validateBeforeSave(): Promise<boolean> {
    if (!this.signatoryAddForm.valid) {
      this.toastService.createNewToast({ text: 'Formulário inválido', type: 'warning', autoHide: true });
      return false;
    }

    if (!this.isWhatsappRequirementMet) {
      this.toastService.createNewToast({
        text: this.translateService.instant('contacts.numberPhoneRequired'),
        type: 'warning',
        autoHide: true
      });
      return false;
    }

    return true;
  }

  private saveToContact(contact: Signatory, isUpdate: boolean): void {
    this.layoutService.toggleLoading(true);
    const { id, tempId } = this.resolveIds();

    if (isUpdate && Number.isInteger(id) && id !== 0) {
      this.signatoryService.update(id as number, contact.toSave())
        .then(() => this.emitAndClose(contact, id, tempId, false))
        .catch(err => this.handleError(err));
    } else {
      this.signatoryService.insert(contact.toSave())
        .then(newId => this.emitAndClose(contact, newId, tempId, false))
        .catch(err => this.handleError(err));
    }
  }

  private saveLocally(contact: Signatory): void {
    const { id, tempId } = this.resolveIds();
    this.addSignatoryToTable.emit(this.buildDto(contact, id, tempId, true));
    this.close();
  }

  private resolveIds(): { id: number | string, tempId: string | null } {
    const temp = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`;

    if (this.userToEdit) {
      const real = +this.userToEdit.id;
      if (!isNaN(real)) {
        return { id: real, tempId: temp };
      } else {
        return { id: this.userToEdit.tempId, tempId: this.userToEdit.id };
      }
    }
    return { id: temp, tempId: null };
  }

  private emitAndClose(contact: Signatory, id: number | string, tempId: string | null, saveJustInEnvelope: boolean): void {
    this.addSignatoryToTable.emit(this.buildDto(contact, id, tempId, saveJustInEnvelope));
    this.close();
    this.layoutService.toggleLoading(false);
  }

  private buildDto(contact: Signatory, id: number | string, tempId: string | null, saveJustInEnvelope: boolean): any {
    return {
      id,
      tempId,
      name: contact.name,
      mail: contact.mail,
      ssn: contact.ssn,
      subscriberType: contact.subscriberType,
      signatureType: contact.signatureType,
      phoneNumber: contact.phoneNumber,
      acceptWhatsApp: contact.acceptWhatsApp,
      allSegments: contact.allSegments,
      segments: contact.segments,
      saveTo: contact.saveTo,
      saveJustInEnvelope: saveJustInEnvelope,
      signatoryTypeRoleId: contact.signatoryTypeRoleId,
      signatoryTypeRole: this.subscriberType.value ?? null,
      isEdited: !!this.userToEdit
    };
  }

  private handleError(err: any): void {
    this.layoutService.toggleLoading(false);
    this.toastService.createNewToast({
      text: 'Erro ao salvar contato',
      type: 'danger',
      autoHide: true
    });
  }

  onSignatureType(id: number | { id: number, value: string }) {
    const extractedId = typeof id === 'object' ? id.id : id;
    return this.tableSignUserBase.signatureType.GetValue(extractedId);
  }

  nextSignatoryOrder(): number {
    const order = Math.max(...this.signatories.map(el => el.order), 0);
    return order + 1;
  }

  cleanForm() {
    this.signatoryAddForm.reset();

    this.fullName.setValue('');
    this.mail.setValue('');
    this.ssn.setValue('');
    this.subscriberType.setValue(null);
    this.signatureType.setValue(null);

    this.telephoneValue = '';
    this.defaultValuePhone = '';
    this.acceptWhatsapp = false;
    this.disabledSSN = false;
    this.hiddenSSN = false;
    this.allSegments = true;

    if (this.segments) {
      this.segments.forEach(segment => segment.checked = true);
    }

    if (this.signatoryAddForm.get('ssn')) {
      this.signatoryAddForm.get('ssn').enable();
    }
  }

  onSubscriberTypeSelectItem(value) {
    this.subscriberType.setValue(value);
    this.subscriberType.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    if (this.subscriberType.invalid) {
      this.subscriberType.markAsTouched();
      this.subscriberType.markAsDirty();
    }
  }

  onSignatureTypeSelectItem(value) {
    this.signatureType.setValue(value);
  }

  private subscriberTypeValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value as SignatoryTypeRole;
    if (!value) {
      return null;
    }
    if (!value.isActive && !this.isObserverOrAuthorizer && !this.isResponsible) {
      return { inactiveSignatoryTypeRole: true };
    }
    return null;
  }

  subscribeOnChangeSsnValue() {
    this.ssn.valueChanges.subscribe((value: string) => this.onSsnValueChange(value));
    this.fullName.valueChanges.subscribe((value: string) => this.onNameValueChange(value));
  }

  onNameValueChange(value) {
    const regexValidate = /[^0-9a-zA-Z çÇ.`'&@áÁéÉíÍóÓúÚâÂêÊôÔàÀüÜãÃõÕñÑ]+/g;

    if (value && regexValidate.test(value)) {
      this.fullName.setValue(value.replace(regexValidate, ''));
    }

    if (value?.replace(regexValidate, '').length >= 49 && !this.signatureWarningLitmitCaracter) {
      this.signatureWarningLitmitCaracter = true;
      const toast: IToast = { text: this.translateService.instant('businessError.signatureWarningLimitCaracter'), autoHide: true, type: 'warning' };
      this.toastService.createNewToast(toast);
    } else if (value?.replace(regexValidate, '').length < 49) {
      this.signatureWarningLitmitCaracter = false;
    }
  }

  setSsnMask(value) {
    this.ssnMask = CPFCNPJUtil.getMask(value);
  }

  onSsnInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const uppercaseValue = (input.value || '').toUpperCase();

    if (input.value !== uppercaseValue) {
      input.value = uppercaseValue;
    }

    if (this.ssn.value !== uppercaseValue) {
      this.ssn.setValue(uppercaseValue);
    }
  }

  onSsnValueChange(value) {
    if (value && !this.userToEdit) {
      if (!IdentificationCodeUtil.isValid(value)) {
        this.ssn.setValue(StringUtil.removeSpecialChars(value));
      }
      setTimeout(() => {
        this.setSsnMask(value);
        if (!this.validarSsn(value)) {
          this.ssn.setErrors({ invalidSSN: true });
        } else {
          this.ssn.setErrors(null);
        }
      });
    }
  }

  validarSsn(value) {
    return CPFCNPJUtil.validate(value);
  }

  modalClose() {
    this.activeModal.dismiss();
  }

  isNumberValid(valid) {
    this.phoneValid = valid;
  }

  someSegmentSelected() {
    if (this.segments != undefined) {
      return this.segments.some(x => x.checked === true) || this.allSegments;
    }
    return false;
  }

  changeAcceptWhatsapp(event: boolean) {
    this.acceptWhatsapp = event;

    const phoneControl = this.signatoryAddForm.get('telephoneValue');
    if (event) {
      phoneControl.setValidators([Validators.required]);
    } else {
      phoneControl.clearValidators();
    }
    phoneControl.updateValueAndValidity();
  }

  toastWhatsFree() {
    const toast: IToast = {
      text: this.translateService.instant('contacts.acceptWhatsappSignFreeToast'),
      autoHide: true,
    };
    this.toastService.createNewToast(toast);
  }
}